import { createFileRoute } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  ArrowRight,
  Zap,
  Package,
  TrendingUp,
  Users,
  DollarSign,
  ShoppingCart,
  Handshake,
  Trophy,
  Quote,
} from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import { HeroSocialProof } from "@/components/ui/hero/HeroSocialProof";
import { Footer } from "@/components/ui/footer";
import { ContactSection } from "@/components/sections/ContactSection";
import { cn } from "@/lib/utils";
import logo from "@/assets/mind-media-logo.svg";
import maingearLogo from "@/assets/maingear-logo.svg";
import maingearFeatured from "@/assets/maingear-featured.webp";
import maingearPc from "@/assets/maingear-pc.webp";

const MAINGEAR_DESCRIPTION =
  "17 years building MAINGEAR — websites, product launches, packaging, advertising, and collaborations that shaped a category-leading PC brand.";

export const Route = createFileRoute("/maingear-case-study")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "MAINGEAR Case Study — 17 Years, One Brand — Mind Media" },
      { name: "description", content: MAINGEAR_DESCRIPTION },
      { property: "og:title", content: "MAINGEAR Case Study — Mind Media" },
      { property: "og:description", content: MAINGEAR_DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://mindmedia.co/maingear-case-study" },
      { property: "og:image", content: maingearFeatured },
    ],
    links: [{ rel: "canonical", href: "https://mindmedia.co/maingear-case-study" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "MAINGEAR Case Study — 17 Years, One Brand",
          description: MAINGEAR_DESCRIPTION,
          image: `https://mindmedia.co${maingearFeatured}`,
          author: { "@type": "Organization", name: "Mind Media" },
          publisher: { "@type": "Organization", name: "Mind Media" },
          mainEntityOfPage: "https://mindmedia.co/maingear-case-study",
        }),
      },
    ],
  }),
  component: MaingearCaseStudyPage,
});

const gradText =
  "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 bg-clip-text text-transparent";

const TIMELINE: {
  range: string;
  title: string;
  body: string;
  items: string[];
}[] = [
  {
    range: "2008 – 2012",
    title: "Learning the Brand",
    body: "Early website work, product launches, marketing assets, trade show graphics, and email campaigns.",
    items: ["THAINGEAR Gaming PC", "SHIFT Series", "Performance Desktops", "Unleash Performance"],
  },
  {
    range: "2013 – 2016",
    title: "Expanding the Experience",
    body: "New website iterations, landing pages, email marketing, SEO initiatives, and product launches.",
    items: [
      "Built for Performance",
      "Desktops Catalog",
      "NK Series Launch",
      "Ultimate Performance",
    ],
  },
  {
    range: "2017 – 2019",
    title: "Products Beyond PCs",
    body: "Designed MAINGEAR FORMA GT chair line, backpacks, mousepads, apparel, accessories, and product launches.",
    items: ["FORMA GT Chair", "Backpacks", "Mousepads", "Apparel & Accessories"],
  },
  {
    range: "2020 – 2024",
    title: "Complete Brand Transformation",
    body: "Led the full rebrand and redesign across every touchpoint of the brand and customer experience.",
    items: ["New Logo System", "Built for Victory", "Packaging Overhaul", "Website Redesign"],
  },
];

const STATS = [
  { Icon: Zap, value: "17", label: "Years of Partnership" },
  { Icon: Package, value: "200+", label: "Projects Completed" },
  { Icon: TrendingUp, value: "Millions", label: "In Revenue Influenced" },
  { Icon: Users, value: "Global", label: "Influencer & Brand Collabs" },
];

const RESULTS = [
  {
    Icon: TrendingUp,
    title: "0 → 16,000+",
    sub: "Monthly Organic Visitors",
  },
  {
    Icon: DollarSign,
    title: "$5K → $20K",
    sub: "Google Ads Budget Scaled Per Month",
  },
  {
    Icon: ShoppingCart,
    title: "Good / Better / Best",
    sub: "Helped transition to a scalable purchasing model",
  },
  {
    Icon: Handshake,
    title: "Strategic Partnerships",
    sub: "Intel, NVIDIA, Microsoft and more",
  },
  {
    Icon: Trophy,
    title: "Award-Winning Results",
    sub: "Industry recognition and growth",
  },
];

const DISCIPLINES = [
  "Brand Strategy",
  "Creative Direction",
  "Web Design",
  "eCommerce Design",
  "UX / UI Design",
  "Product Design",
  "Packaging Design",
  "Apparel Design",
  "Email Marketing",
  "SEO",
  "Google Ads",
  "Campaign Creative",
  "Photography Direction",
  "Video Direction",
  "Landing Pages",
  "Art Direction",
];

const PARTNERS = ["Intel", "NVIDIA", "Microsoft", "AMD", "HP", "Razer"];

const COLLABS = [
  { name: "deadmau5", role: "Custom build & campaign" },
  { name: "STEVE AOKI", role: "Custom build & campaign" },
  { name: "ZEDD", role: "Custom build & campaign" },
  { name: "PEWDIEPIE", role: "Custom build & campaign" },
];

function MaingearCaseStudyPage() {
  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative overflow-hidden pb-20">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-32 -top-40 h-[520px] w-[520px] rounded-full bg-hero-glow-warm/30 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 -top-32 h-[460px] w-[460px] rounded-full bg-hero-glow-cool/30 blur-3xl"
        />
        <HeroNav
          logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
          navigation={[
            { label: "Work", href: "/#work" },
            { label: "About", href: "/#about" },
            { label: "Services", href: "/#pricing" },
            { label: "Pricing", href: "/#pricing" },
            { label: "Contact", href: "#contact" },
          ]}
          ctaButton={{ label: "Book a Meeting", href: "#contact" }}
        />

        <div className="relative mx-auto px-6 mt-12 grid max-w-7xl items-center gap-12 sm:mt-16 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <div className="flex items-center justify-between gap-4">
              <span className="inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand-blue">
                MAINGEAR
              </span>
              <span className="hidden text-xs font-bold uppercase tracking-[0.22em] text-muted-foreground sm:inline">
                Case Study
              </span>
            </div>
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-foreground sm:text-6xl">
              17 Years. One Brand.
              <br />
              Countless Ways to <span className={gradText}>Win.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground sm:text-xl">
              From brand strategy and websites to product launches, packaging, advertising, and
              collaborations with the world's biggest artists and influencers.
            </p>

            {/* STATS */}
            <div className="mt-10 grid grid-cols-2 gap-3 rounded-2xl border border-border bg-card/60 p-4 sm:grid-cols-4 sm:p-5">
              {STATS.map(({ Icon, value, label }) => (
                <div key={label} className="flex flex-col gap-2 px-2 py-1">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-4 w-4 text-brand-blue" strokeWidth={2.5} />
                  </span>
                  <div className={cn("text-2xl font-bold tracking-tight", gradText)}>{value}</div>
                  <div className="text-xs font-medium text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>

            {/* PARTNERS */}
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                Strategic partners
              </span>
              {PARTNERS.map((p) => (
                <span key={p} className="text-sm font-bold text-foreground/80">
                  {p}
                </span>
              ))}
            </div>

            <div className="mt-8">
              <HeroSocialProof />
            </div>
          </div>

          <div className="relative">
            <div
              aria-hidden
              className="absolute inset-0 -z-10 rounded-[2rem] bg-gradient-to-br from-brand-blue/20 via-purple-500/20 to-pink-500/20 blur-2xl"
            />
            <div className="overflow-hidden rounded-[2rem] border border-border bg-card shadow-2xl shadow-purple-500/20">
              <img
                src={maingearPc}
                alt="MAINGEAR custom PC"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-card px-4 py-2 shadow-lg">
              <img src={maingearLogo} alt="MAINGEAR" className="h-4 w-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kicker="The Journey"
            title={
              <>
                17 years of <span className={gradText}>building together.</span>
              </>
            }
          >
            From early product launches to a complete brand transformation, here's how the MAINGEAR
            partnership grew chapter by chapter.
          </SectionHead>

          <div className="relative space-y-12">
            <div
              aria-hidden
              className="absolute left-3 top-2 bottom-2 hidden w-[3px] -translate-x-1/2 bg-gradient-to-b from-brand-blue via-purple-500 to-pink-500 animate-gradient-flow md:block"
            />
            {TIMELINE.map((t, i) => (
              <div key={t.range} className="grid gap-6 md:grid-cols-[1fr_2fr] md:gap-10">
                <div className="relative md:pl-10">
                  <span
                    aria-hidden
                    className="absolute left-3 top-2 hidden h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full bg-background md:flex"
                  >
                    <span className="h-3 w-3 rounded-full bg-gradient-to-b from-brand-blue via-purple-500 to-pink-500 animate-gradient-flow" />
                  </span>
                  <div className={cn("text-sm font-bold uppercase tracking-[0.18em]", gradText)}>
                    {t.range}
                  </div>
                  <h3 className="mt-2 text-2xl font-bold tracking-tight">{t.title}</h3>
                  <p className="mt-2 text-[15px] text-muted-foreground">{t.body}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {t.items.map((label, j) => (
                    <div
                      key={label}
                      className="group relative aspect-[4/5] overflow-hidden rounded-xl border border-border bg-gradient-to-br from-zinc-900 via-purple-950/40 to-zinc-900 shadow-lg"
                    >
                      <div
                        aria-hidden
                        className="absolute inset-0 opacity-30"
                        style={{
                          background: `radial-gradient(circle at ${30 + j * 15}% ${40 + i * 10}%, rgba(168,85,247,0.4), transparent 60%)`,
                        }}
                      />
                      <div className="absolute inset-0 flex items-end p-3">
                        <span className="text-xs font-bold uppercase tracking-wide text-white/90">
                          {label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ARTIST & INFLUENCER COLLABS */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-start gap-12 lg:grid-cols-[1fr_2fr]">
            <div>
              <Kicker>Collaborations</Kicker>
              <h2 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                Artist & Influencer <span className={gradText}>Collaborations.</span>
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Custom PC builds and campaigns with world-class talent and global brands.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {COLLABS.map((c, i) => (
                <div
                  key={c.name}
                  className="group relative aspect-[3/4] overflow-hidden rounded-xl border border-border shadow-lg"
                >
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(160deg, oklch(0.2 0.05 ${260 + i * 20}), oklch(0.35 0.15 ${280 + i * 25}))`,
                    }}
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
                  />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <div className="text-xs uppercase tracking-[0.18em] text-white/70">Artist</div>
                    <div className="mt-1 text-lg font-bold text-white">{c.name}</div>
                    <div className="mt-1 text-xs text-white/70">{c.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kicker="The Results"
            title={
              <>
                Real numbers. <span className={gradText}>Real growth.</span>
              </>
            }
          />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {RESULTS.map(({ Icon, title, sub }) => (
              <div key={title} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                  <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                </span>
                <div className={cn("mt-4 text-xl font-bold leading-tight", gradText)}>{title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT I ACTUALLY DID */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
            <div className="rounded-3xl border border-border bg-card p-8 shadow-sm">
              <Kicker>Scope of Work</Kicker>
              <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                What I <span className={gradText}>actually did.</span>
              </h2>
              <p className="mt-3 text-muted-foreground">
                A 17-year partnership touches everything. Here's the disciplines I led across the
                MAINGEAR brand.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-2">
                {DISCIPLINES.map((d) => (
                  <div
                    key={d}
                    className="flex items-center gap-2 rounded-lg border border-border bg-background/50 px-3 py-2 text-sm font-medium"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-brand-blue to-pink-500" />
                    {d}
                  </div>
                ))}
              </div>
            </div>

            {/* TESTIMONIAL */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-blue via-purple-600 to-pink-500 p-10 text-white shadow-2xl shadow-purple-500/30">
              <Quote className="h-10 w-10 text-white/70" />
              <p className="mt-4 text-xl font-medium leading-relaxed sm:text-2xl">
                "Mind Media has been an incredible partner. They took the time to truly understand
                our brand and built a site that not only looks amazing but performs at the highest
                level. The results speak for themselves."
              </p>
              <div className="mt-8 flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur">
                  <img src={maingearLogo} alt="MAINGEAR" className="h-4 w-auto invert" />
                </div>
                <div>
                  <div className="text-base font-bold">Wallace Santos</div>
                  <div className="text-sm text-white/80">CEO, MAINGEAR</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="final-cta" className="relative overflow-hidden px-6 py-20 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 left-1/2 h-[420px] w-[700px] -translate-x-1/2 rounded-full bg-hero-glow-warm/25 blur-3xl"
        />
        <div className="relative mx-auto max-w-2xl lg:max-w-none">
          <h2 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Let's build something <span className={gradText}>exceptional together.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-lg text-muted-foreground">
            17 years with MAINGEAR proves what a real partnership can do. Let's see what we can
            build for you.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-6 py-3 text-sm font-bold text-brand-blue-foreground shadow-lg shadow-brand-blue/40 transition hover:-translate-y-0.5 hover:bg-brand-blue-hover"
            >
              Start your project <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="/#work"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-bold text-foreground transition hover:-translate-y-0.5 hover:bg-accent"
            >
              See more work
            </a>
          </div>
        </div>
      </section>

      <ContactSection />

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/", label: "Home" },
          { href: "/branding", label: "Branding" },
          { href: "/seo", label: "SEO" },
          { href: "/#pricing", label: "Pricing" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-brand-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  children,
}: {
  kicker: string;
  title: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="mx-auto mb-12 max-w-3xl text-center">
      <Kicker>{kicker}</Kicker>
      <h2 className="mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
        {title}
      </h2>
      {children && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{children}</p>}
    </div>
  );
}
